import { ApolloError, gql } from '@apollo/client';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconFloppyDisk from '@apollo/icons/default/IconFloppyDisk.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  IconButton,
  KbdCombo,
  Link,
} from '@apollo/orbit';
import copy from 'copy-to-clipboard';
import React, { useState } from 'react';

import { isMac } from 'src/app/graph/explorerPage/helpers/isMac';
import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  hotkeys,
  useKeypressListener,
} from 'src/hooks/useKeypressListener/useKeypressListener';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { computeJsonSchema } from './computeJsonSchema';

export const CloudRouterConfigEditorFragment = gql`
  fragment CloudRouterConfigEditorFragment on GraphVariant {
    id
    routerConfig
    router {
      id
      routerVersion {
        configSchema
      }
    }
    subgraphs {
      name
    }
  }
`;

export const RouterConfigEditor = ({
  defaultValue,
  handleSaveRouterConfig,
  canEditBuildConfig,
  canQueryBuildConfig,
  upsertRouterError,
  upsertResponseData,
  configSchema,
  allSubgraphs,
}: {
  defaultValue: string | undefined;
  handleSaveRouterConfig: (config: string) => Promise<void>;
  canEditBuildConfig?: boolean;
  canQueryBuildConfig?: boolean;
  upsertRouterError?: ApolloError;
  upsertResponseData?:
    | GraphQLTypes.UpdateRouterConfigMutation
    | null
    | undefined;
  configSchema: string | undefined;
  allSubgraphs: string[];
}) => {
  const [shouldShowErrors, setShouldShowErrors] = useState(false);
  const [shouldShowEditorActions, setShouldShowEditorActions] =
    React.useState(false);
  const [shouldPromptCopySuccess, setShouldPromptCopySuccess] =
    React.useState(false);

  const schemaInfo = React.useMemo(() => {
    return typeof configSchema === 'string'
      ? {
          id: 'routerConfig',
          schema: computeJsonSchema(configSchema, allSubgraphs),
          language: 'yaml' as const,
        }
      : undefined;
  }, [allSubgraphs, configSchema]);

  const editor = useStudioEditor({
    language: 'yaml',
    defaultValue,
    glyphMargin: false,
    readOnly: !canEditBuildConfig,
  });

  const model = useEditorModel(editor);

  React.useEffect(() => {
    if (shouldPromptCopySuccess) {
      const timeout = setTimeout(() => {
        setShouldPromptCopySuccess(false);
      }, 1_000);
      return () => clearTimeout(timeout);
    }
  }, [shouldPromptCopySuccess]);

  React.useEffect(() => {
    // This should run every time the defaultValue changes (i.e. the user saves)
    // so we want to hide the editor actions after save to indicate that the change
    // went through.
    setShouldShowEditorActions(false);
    const changeContentEmitter = model?.onDidChangeContent(() => {
      setShouldShowEditorActions(defaultValue !== model.getValue());
    });
    return () => changeContentEmitter?.dispose();
  }, [defaultValue, model]);

  useKeypressListener(hotkeys.saveOperation, (e) => {
    if (shouldShowEditorActions && model) {
      handleSaveRouterConfig(model.getValue());
    }

    e?.preventDefault();
    e?.stopPropagation();
  });

  const upsertRouterValidationError =
    upsertResponseData?.service?.variant?.upsertRouterConfig?.__typename ===
    'RouterUpsertFailure'
      ? upsertResponseData.service.variant.upsertRouterConfig.message
      : undefined;

  React.useEffect(() => {
    if (upsertRouterValidationError || upsertRouterError) {
      setShouldShowErrors(true);
    }
  }, [upsertRouterValidationError, upsertRouterError]);

  return (
    <Card isDivided className="mb-2 overflow-hidden">
      <CardHeader>
        <CardTitle>Router configuration YAML</CardTitle>
        <CardDescription>
          {canEditBuildConfig || canQueryBuildConfig ? (
            <>
              Set custom header and CORS configuration.{' '}
              <Link isExternal to={DocsRouter.path('CloudRouterYAMLConfig')}>
                Learn more
              </Link>
            </>
          ) : (
            <>
              You do not have permission to view this information. Please
              contact one of your org admins.
            </>
          )}
        </CardDescription>
        {canEditBuildConfig && shouldShowEditorActions && model && (
          <ButtonGroup>
            <Button
              variant="hidden"
              onClick={() => {
                editor.setValue(defaultValue ?? '');
                // we should hide any existing errors when the user resets
                // since they are going back to a clean slate
                setShouldShowErrors(false);
              }}
            >
              Reset
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSaveRouterConfig(model.getValue())}
              leftIcon={<IconFloppyDisk />}
            >
              Save
            </Button>
          </ButtonGroup>
        )}
      </CardHeader>
      {(canQueryBuildConfig || shouldShowErrors) && (
        <CardBody>
          {shouldShowErrors &&
            !!(upsertRouterError?.message || upsertRouterValidationError) && (
              <ErrorMessage>{upsertRouterValidationError}</ErrorMessage>
            )}
          {canQueryBuildConfig && (
            <>
              <div className="relative">
                <Tooltip
                  label={
                    shouldPromptCopySuccess
                      ? 'Copied! 🎉'
                      : 'Share current router configuration'
                  }
                >
                  <IconButton
                    aria-label="Copy"
                    className="absolute right-0 top-0 z-20"
                    variant="secondary"
                    onClick={() => {
                      copy(editor.getValue());
                      setShouldPromptCopySuccess(true);
                    }}
                  >
                    <IconCopy />
                  </IconButton>
                </Tooltip>
                <StudioEditorContainer
                  className="h-120"
                  editor={editor}
                  schemaInfo={schemaInfo}
                />
              </div>
            </>
          )}
        </CardBody>
      )}
      {canEditBuildConfig && (
        <CardFooter className="justify-end gap-2 text-secondary">
          {isMac ? (
            <KbdCombo keys={['⌘', 'S']} />
          ) : (
            <KbdCombo keys={['control', 'S']} />
          )}{' '}
          to save
        </CardFooter>
      )}
    </Card>
  );
};
